import { infiniteScrollEnabled as infiniteScrollEnabledDefault } from 'BuildArtifacts/featureFlags';

import pageType from 'Addons/search/pageType';
import { mainConfig, type PageConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import defaultUiConfig from 'Platforms/woocommerce/uiConfig';
import { isObjectRecord, type ObjectRecord } from 'Utils/guards';
import { trimEndSlash } from 'Utils/string';
import { addPlatformRoute } from 'Utils/url';

const {
  AfterInit: rawAfterInit,
  collapsibleAnimationTime = 500,
  colorGroups,
  colorStripes,
  customFacetValueFilter,
  customMessage = {},
  customUriParser = {},
  defaultView = '',
  disableRequestScroll,
  FacetsCollapsedDefault,
  groupColorsBy,
  infiniteScrollEnabled = infiniteScrollEnabledDefault,
  noResultsRedirectTimeout = 0,
  page = {},
  pagination: rawPaginationSettings = {},
  platform,
  scrollDetection,
  SearchPageBaseUrl = '',
  searchPageUrl: customSearchPageUrl,
  sortFields,
  stickyHeaderSelector,
  useLocationHash,
  Widgets,
} = mainConfig;

type DefaultUiConfigShape = {
  page?: PageConfig;
  searchPageUrl?: string;
  isInDesignMode?: boolean;
  themeInfo?: ThemeInfoMap;
};

interface ThemeInfoMap {
  id?: string | number;
  name?: string;
  role?: string;
  schema_name?: string;
  schema_version?: string;
}

const {
  page: pageDefaults = {},
  searchPageUrl: defaultSearchPageUrl,
  isInDesignMode,
  themeInfo = {},
} = normalizeDefaultUiConfig(defaultUiConfig);

const typeToSelectorMap = { ...(pageDefaults.typeToSelectorMap || {}), ...page.typeToSelectorMap };

function afterInit(): void {
  if (rawAfterInit) {
    try {
      rawAfterInit();
    } catch (e) {
      console.warn(e);
    }
  }
}

const searchPageUrl = addPlatformRoute(customSearchPageUrl || defaultSearchPageUrl);

function getPageType() {
  if (trimEndSlash(window.location.pathname) === trimEndSlash(searchPageUrl)) {
    return pageType.search;
  }

  const getType = page.getPageType || pageDefaults.getPageType;
  const type = getType?.(pageDefaults.getPageType?.defaults ?? { getter: () => pageType.unknown });

  if (type && type !== pageType.unknown) {
    return type;
  }

  const extractedPageType = Object.keys(typeToSelectorMap).find((key) =>
    window.document.querySelector(typeToSelectorMap[key]),
  );

  return extractedPageType || pageType.unknown;
}

const uiConfig = {
  afterInit,
  collapsibleAnimationTime,
  colorPaletteConfig: { colorGroups, colorStripes, groupColorsBy },
  customFacetValueFilter,
  customMessage,
  customUriParser: { parsingNeeded: (): boolean => false, ...customUriParser },
  defaultView,
  disableRequestScroll,
  facetsCollapsedDefault: FacetsCollapsedDefault,
  isInDesignMode,
  infiniteScrollEnabled,
  noResultsRedirectTimeout,
  pageType: getPageType(),
  pagination: { edgeRange: 1, centralRange: 2, ...rawPaginationSettings },
  platform,
  scrollDetection,
  searchPageBaseUrl: SearchPageBaseUrl,
  searchPageUrl,
  sortFields,
  stickyHeaderSelector,
  themeInfo,
  useLocationHash,
  widgetsConsideredInScroll: Widgets.filter((w) => w.consideredInScroll),
};

window.Convermax.templates!.config.uiConfig = uiConfig;

export default uiConfig;

function normalizeDefaultUiConfig(config: unknown): DefaultUiConfigShape {
  if (!isObjectRecord(config)) {
    return {};
  }

  return {
    page: getDefaultPageConfig(config.page),
    searchPageUrl: typeof config.searchPageUrl === 'string' ? config.searchPageUrl : undefined,
    isInDesignMode: typeof config.isInDesignMode === 'boolean' ? config.isInDesignMode : undefined,
    themeInfo: getThemeInfo(config.themeInfo),
  };
}

function getDefaultPageConfig(page: unknown): DefaultUiConfigShape['page'] {
  if (!isObjectRecord(page)) {
    return undefined;
  }

  return {
    getPageType: isPageTypeGetter(page.getPageType) ? page.getPageType : undefined,
    typeToSelectorMap: getStringRecord(page.typeToSelectorMap),
  };
}

function isPageTypeGetter(value: unknown): value is NonNullable<DefaultUiConfigShape['page']>['getPageType'] {
  return typeof value === 'function';
}

function getThemeInfo(themeInfo: unknown): ThemeInfoMap | undefined {
  if (!isObjectRecord(themeInfo)) {
    return undefined;
  }

  const result: ThemeInfoMap = {
    id: typeof themeInfo.id === 'string' || typeof themeInfo.id === 'number' ? themeInfo.id : undefined,
    name: typeof themeInfo.name === 'string' ? themeInfo.name : undefined,
    role: typeof themeInfo.role === 'string' ? themeInfo.role : undefined,
  };

  copyStringProperty(result, themeInfo, 'schema_name');
  copyStringProperty(result, themeInfo, 'schema_version');

  return result;
}

function copyStringProperty(target: ThemeInfoMap, source: ObjectRecord, property: keyof ThemeInfoMap): void {
  const value = source[property];

  if (typeof value === 'string') {
    target[property] = value;
  }
}

function getStringRecord(value: unknown): Record<string, string> | undefined {
  if (!isObjectRecord(value)) {
    return undefined;
  }

  const result: Record<string, string> = {};

  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry !== 'string') {
      return undefined;
    }

    result[key] = entry;
  }

  return result;
}
